import { useState } from 'react';
import { useWorkspaceStore } from '@/stores/workspace-store';
import { workspaceApi } from '@/lib/api/workspace';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';

interface WorkspaceSettingsPageProps {
  workspaceId: string;
}

export function WorkspaceSettingsPage({ workspaceId }: WorkspaceSettingsPageProps) {
  const workspaces = useWorkspaceStore((s) => s.workspaces);
  const workspace = workspaces.find((w) => w.id === workspaceId);

  const [name, setName] = useState(workspace?.name ?? '');
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  if (!workspace) {
    return (
      <div className="text-muted-foreground flex min-h-[40vh] items-center justify-center text-sm">
        Workspace not found.
      </div>
    );
  }

  const handleSave = async () => {
    if (!name.trim()) return;
    setIsSaving(true);
    setSaveSuccess(false);
    try {
      await workspaceApi.updateWorkspace(workspaceId, { name: name.trim() });
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2000);
    } catch {
      toast.error('Failed to save changes.');
    } finally {
      setIsSaving(false);
    }
  };

  const isDirty = name.trim() !== workspace.name;

  return (
    <div className="w-3xl mx-auto min-h-full flex-1 space-y-10 px-8 py-10 xl:border-x">
      <div>
        <h1 className="text-xl font-semibold tracking-tight">Workspace</h1>
        <p className="text-muted-foreground mt-1 text-sm">
          Manage your workspace details and settings.
        </p>
      </div>

      <Separator />

      {/* General */}
      <div className="space-y-6">
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="workspace-name">Workspace name</Label>
            <Input
              id="workspace-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter workspace name"
              className="max-w-sm"
            />
          </div>

          <div className="space-y-2">
            <Label>Plan</Label>
            <div className="flex items-center gap-2">
              <span className="bg-muted text-foreground rounded-md px-2 py-1 text-xs font-medium capitalize">
                {workspace.plan}
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Button onClick={handleSave} disabled={!isDirty || isSaving} size="sm">
            {isSaving ? 'Saving…' : 'Save changes'}
          </Button>
          {saveSuccess && <span className="text-muted-foreground text-sm">Saved.</span>}
        </div>
      </div>

      <Separator />

      {/* Danger zone */}
      <div className="space-y-4">
        <div>
          <h2 className="text-base font-medium">Danger zone</h2>
          <p className="text-muted-foreground mt-1 text-sm">
            Permanently delete this workspace and all of its data.
          </p>
        </div>
        <AlertDialog>
          <AlertDialogTrigger render={<Button variant="destructive" size="sm" />}>
            Delete workspace
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete workspace?</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently delete <strong>{workspace.name}</strong> and all of its data.
                This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                onClick={async () => {
                  await workspaceApi.deleteWorkspace(workspaceId);
                  window.location.href = '/';
                }}
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}
